import React from "react";
import { useState } from "react";
import {
    ArrowLeft,
    ChevronDown,
    Phone,
    Mail,
    HelpCircle,
    Search,
    ExternalLink,
    Menu as MenuIcon,
    X as CloseIcon
} from "lucide-react";
import {
    Button,
    Input,
    Typography,
    Box,
    Grid,
    Container,
    Paper,
    Chip,
    InputAdornment,
    Divider,
    Link,
    Drawer,
    IconButton
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { guides } from "./helpGuides";
import { faqs, categories } from "./helpFAQs";
import FormattedText from "./FormattedText";

const useStyles = makeStyles(theme => ({
    root: {
        minHeight: "100vh",
        backgroundColor: "#FAFBFC",
        [theme.breakpoints.down("xs")]: {
            "& .MuiTypography-h3": { fontSize: "1.6rem" },
            "& .MuiTypography-h4": { fontSize: "1.35rem" },
            "& .MuiTypography-h5": { fontSize: "1.15rem" },
            "& .MuiTypography-h6": { fontSize: "1rem" }
        }
    },
    header: {
        backgroundColor: "white",
        borderBottom: "1px solid #E8E8E8",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        // Keeps the menu button reachable while reading a long guide.
        [theme.breakpoints.down("md")]: {
            position: "sticky",
            top: 0,
            zIndex: theme.zIndex.appBar
        }
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: 16,
        minWidth: 0,
        [theme.breakpoints.down("xs")]: { gap: 4 }
    },
    headerDivider: {
        width: 1,
        height: 24,
        backgroundColor: "#E8E8E8",
        [theme.breakpoints.down("xs")]: { display: "none" }
    },
    hideOnPhone: {
        [theme.breakpoints.down("xs")]: { display: "none" }
    },
    menuButton: {
        color: "#1DB584",
        [theme.breakpoints.up("lg")]: { display: "none" }
    },
    headerContent: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 64,
        padding: "0 24px",
        [theme.breakpoints.down("xs")]: {
            height: 56,
            padding: "0 8px"
        }
    },
    backButton: {
        color: "#666",
        textTransform: "none",
        fontWeight: 500,
        "&:hover": {
            backgroundColor: "rgba(29, 181, 132, 0.1)"
        }
    },
    title: {
        fontWeight: "bold",
        fontSize: "1.25rem",
        color: "#1DB584",
        whiteSpace: "nowrap",
        [theme.breakpoints.down("xs")]: { fontSize: "1rem" }
    },
    searchInput: {
        width: 300,
        "& .MuiOutlinedInput-root": {
            borderRadius: 8
        },
        [theme.breakpoints.down("md")]: {
            display: "none"
        }
    },
    sidebarColumn: {
        [theme.breakpoints.down("md")]: { display: "none" }
    },
    sidebar: {
        position: "sticky",
        top: 32
    },
    drawerPaper: {
        width: 320,
        maxWidth: "88vw",
        backgroundColor: "#FAFBFC"
    },
    drawerHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 8px 0 16px"
    },
    drawerSearch: {
        margin: "8px 16px 12px",
        width: "calc(100% - 32px)"
    },
    drawerBody: {
        overflowY: "auto",
        padding: "0 12px 16px",
        "& > .MuiPaper-root": { boxShadow: "none", padding: 12 }
    },
    sidebarCard: {
        padding: 24,
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
    },
    navButton: {
        width: "100%",
        justifyContent: "flex-start",
        textTransform: "none",
        padding: "12px 16px",
        marginBottom: 8,
        borderRadius: 8,
        color: "#666",
        "&:hover": {
            backgroundColor: "#F5F5F5"
        }
    },
    activeNavButton: {
        backgroundColor: "#E6F7F1",
        color: "#1DB584",
        border: "1px solid #B8E6D3",
        "&:hover": {
            backgroundColor: "#E6F7F1"
        }
    },
    quickAccessButton: {
        width: "100%",
        justifyContent: "flex-start",
        textTransform: "none",
        padding: "8px 12px",
        marginBottom: 4,
        borderRadius: 6,
        fontSize: "0.875rem",
        color: "#666",
        "&:hover": {
            backgroundColor: "#F5F5F5"
        }
    },
    activeQuickButton: {
        backgroundColor: "#E6F7F1",
        color: "#1DB584"
    },
    // Vertical only: the Container's side gutters must stay, or Grid's negative spacing margins overflow the page.
    mainContent: {
        paddingTop: 32,
        paddingBottom: 32,
        [theme.breakpoints.down("xs")]: { paddingTop: 16, paddingBottom: 16 }
    },
    contentCard: {
        padding: 32,
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        [theme.breakpoints.down("xs")]: { padding: 16 }
    },
    guideHeader: {
        display: "flex",
        alignItems: "center",
        gap: 16,
        marginBottom: 24,
        [theme.breakpoints.down("xs")]: { gap: 12, marginBottom: 16 }
    },
    guideIcon: {
        width: 48,
        height: 48,
        backgroundColor: "#E6F7F1",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#1DB584"
    },
    stepContainer: {
        display: "flex",
        gap: 24,
        marginBottom: 32,
        [theme.breakpoints.down("xs")]: { gap: 12, marginBottom: 24 }
    },
    stepNumber: {
        width: 40,
        height: 40,
        backgroundColor: "#1DB584",
        color: "white",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        flexShrink: 0,
        [theme.breakpoints.down("xs")]: { width: 32, height: 32 }
    },
    stepContent: {
        flex: 1,
        minWidth: 0,
        overflowWrap: "anywhere"
    },
    stepTitle: {
        fontWeight: 600,
        fontSize: "1.125rem",
        color: "#333",
        marginBottom: 8
    },
    screenshotContainer: {
        backgroundColor: "#F5F5F5",
        border: "2px dashed #D1D5DB",
        borderRadius: 8,
        padding: 16,
        textAlign: "center"
    },
    screenshot: {
        width: "100%",
        borderRadius: 8
    },
    faqCard: {
        padding: 24,
        overflowWrap: "anywhere",
        [theme.breakpoints.down("xs")]: { padding: 16 },
        marginBottom: 16,
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        transition: "box-shadow 0.3s ease",
        "&:hover": {
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)"
        }
    },
    contactCard: {
        padding: 32,
        [theme.breakpoints.down("xs")]: { padding: 20 },
        textAlign: "center",
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        transition: "box-shadow 0.3s ease",
        "&:hover": {
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)"
        }
    },
    contactIcon: {
        width: 64,
        height: 64,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 16px"
    },
    emailIcon: {
        backgroundColor: "#E6F7F1",
        color: "#1DB584"
    },
    phoneIcon: {
        backgroundColor: "#EBF4FF",
        color: "#4A90E2"
    },
    contactButton: {
        width: "100%",
        padding: "12px 24px",
        borderRadius: 8,
        textTransform: "none",
        fontWeight: 600
    },
    emailButton: {
        backgroundColor: "#1DB584",
        color: "white",
        "&:hover": {
            backgroundColor: "#17A474"
        }
    },
    phoneButton: {
        backgroundColor: "#4A90E2",
        color: "white",
        "&:hover": {
            backgroundColor: "#357ABD"
        }
    },
    emergencyCard: {
        background: "linear-gradient(135deg, #E6F7F1 0%, #EBF4FF 100%)",
        border: "1px solid #B8E6D3",
        padding: 24,
        borderRadius: 12,
        textAlign: "center",
        marginTop: 24,
        [theme.breakpoints.down("xs")]: { padding: 16 }
    },
    emergencyActions: {
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 12
    },
    categoryCard: {
        padding: 24,
        [theme.breakpoints.down("xs")]: { padding: 16 },
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        cursor: "pointer",
        transition: "all 0.3s ease",
        "&:hover": {
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            transform: "translateY(-2px)"
        }
    },
    categoryHeader: {
        display: "flex",
        alignItems: "center",
        gap: 16,
        marginBottom: 16
    },
    categoryIcon: {
        width: 48,
        height: 48,
        backgroundColor: "#E6F7F1",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#1DB584"
    },
    difficultyChip: {
        fontSize: "0.75rem",
        height: 24
    },
    easyChip: {
        backgroundColor: "#D1FAE5",
        color: "#065F46"
    },
    mediumChip: {
        backgroundColor: "#FEF3C7",
        color: "#92400E"
    },
    hardChip: {
        backgroundColor: "#FEE2E2",
        color: "#991B1B"
    },
    linkText: {
        color: "#1DB584",
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline"
        }
    }
}));

export default function HelpPage() {
    const classes = useStyles();
    const [activeSection, setActiveSectionState] = useState("overview");
    const [searchQuery, setSearchQuery] = useState("");
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    // Every navigation closes the mobile menu and returns to the top of the new content.
    const setActiveSection = id => {
        setActiveSectionState(id);
        setMobileNavOpen(false);
        window.scrollTo(0, 0);
    };

    const activeGuide = guides.find(guide => guide.id === activeSection);
    const filteredGuides = guides.filter(
        guide =>
            guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            guide.category.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getDifficultyChipClass = difficulty => {
        switch (difficulty) {
            case "Easy":
                return classes.easyChip;
            case "Medium":
                return classes.mediumChip;
            case "Hard":
                return classes.hardChip;
            default:
                return "";
        }
    };

    const renderFAQAnswer = answer => {
        // Check if answer contains a URL
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts = answer.split(urlRegex);

        return parts.map((part, index) => {
            if (part.match(urlRegex)) {
                return (
                    <Link
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.linkText}
                    >
                        {part}
                    </Link>
                );
            }
            return part;
        });
    };

    const sidebarNav = (
                            <Paper className={classes.sidebarCard}>
                                <Typography
                                    variant="h6"
                                    style={{
                                        marginBottom: 16,
                                        fontWeight: 600
                                    }}
                                >
                                    Navigation
                                </Typography>

                                {/* Categories */}
                                <div style={{ marginBottom: 24 }}>
                                    {categories.map(category => (
                                        <Button
                                            key={category.id}
                                            className={`${classes.navButton} ${
                                                activeSection === category.id ||
                                                (category.id ===
                                                    "basic-operations" &&
                                                    guides.find(
                                                        g =>
                                                            g.id ===
                                                            activeSection
                                                    )?.category ===
                                                        "Basic Operations") ||
                                                (category.id === "work-order" &&
                                                    guides.find(
                                                        g =>
                                                            g.id ===
                                                            activeSection
                                                    )?.category ===
                                                        "Work Order") ||
                                                (category.id ===
                                                    "asset-management" &&
                                                    guides.find(
                                                        g =>
                                                            g.id ===
                                                            activeSection
                                                    )?.category ===
                                                        "Asset Management") ||
                                                (category.id ===
                                                    "service-request" &&
                                                    guides.find(
                                                        g =>
                                                            g.id ===
                                                            activeSection
                                                    )?.category ===
                                                        "Service Request") ||
                                                (category.id ===
                                                    "sla-management" &&
                                                    guides.find(
                                                        g =>
                                                            g.id ===
                                                            activeSection
                                                    )?.category ===
                                                        "SLA Management") ||
                                                (category.id ===
                                                    "administration" &&
                                                    guides.find(
                                                        g =>
                                                            g.id ===
                                                            activeSection
                                                    )?.category ===
                                                        "Administration")
                                                    ? classes.activeNavButton
                                                    : ""
                                            }`}
                                            startIcon={category.icon}
                                            onClick={() =>
                                                setActiveSection(category.id)
                                            }
                                        >
                                            {category.name}
                                        </Button>
                                    ))}
                                </div>

                                <Divider style={{ margin: "16px 0" }} />

                                {/* Quick Access */}
                                <Typography
                                    variant="subtitle2"
                                    style={{
                                        marginBottom: 12,
                                        color: "#666",
                                        fontWeight: 500
                                    }}
                                >
                                    Quick Access
                                </Typography>
                                <div>
                                    {filteredGuides.map(guide => (
                                        <Button
                                            key={guide.id}
                                            className={`${
                                                classes.quickAccessButton
                                            } ${
                                                activeSection === guide.id
                                                    ? classes.activeQuickButton
                                                    : ""
                                            }`}
                                            startIcon={guide.icon}
                                            onClick={() =>
                                                setActiveSection(guide.id)
                                            }
                                        >
                                            {guide.title}
                                        </Button>
                                    ))}
                                </div>
                            </Paper>
    );

    return (
        <div className={classes.root}>
            {/* Header */}
            <Paper className={classes.header} elevation={0}>
                <div className={classes.headerContent}>
                    <div className={classes.headerLeft}>
                        <IconButton
                            className={classes.menuButton}
                            aria-label="Open help menu"
                            aria-expanded={mobileNavOpen}
                            onClick={() => setMobileNavOpen(true)}
                        >
                            <MenuIcon size={22} />
                        </IconButton>
                        <Button
                            className={classes.backButton}
                            startIcon={<ArrowLeft size={16} />}
                            href="/"
                        >
                            Back<span className={classes.hideOnPhone}> to Home</span>
                        </Button>
                        <div className={classes.headerDivider} />
                        <Typography className={classes.title}>
                            ANIKE Help Center
                        </Typography>
                    </div>
                    <Input
                        className={classes.searchInput}
                        placeholder="Search guides..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        startAdornment={
                            <InputAdornment position="start">
                                <Search size={16} color="#999" />
                            </InputAdornment>
                        }
                    />
                </div>
            </Paper>

            <Container maxWidth="xl" className={classes.mainContent}>
                <Grid container spacing={4}>
                    {/* Sidebar */}
                    <Grid item xs={12} lg={3} className={classes.sidebarColumn}>
                        <div className={classes.sidebar}>
                            {sidebarNav}
                        </div>
                    </Grid>

                    {/* Main Content */}
                    <Grid item xs={12} lg={9}>
                        {/* Guide Content */}
                        {activeGuide && (
                            <Paper className={classes.contentCard}>
                                <div className={classes.guideHeader}>
                                    <div className={classes.guideIcon}>
                                        {activeGuide.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <Typography
                                            variant="h4"
                                            style={{
                                                fontWeight: 700,
                                                marginBottom: 8
                                            }}
                                        >
                                            {activeGuide.title}
                                        </Typography>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8
                                            }}
                                        >
                                            <Chip
                                                label={activeGuide.difficulty}
                                                size="small"
                                                className={`${
                                                    classes.difficultyChip
                                                } ${getDifficultyChipClass(
                                                    activeGuide.difficulty
                                                )}`}
                                            />
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {activeGuide.category}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    {activeGuide.steps.map((step, index) => (
                                        <div
                                            key={index}
                                            className={classes.stepContainer}
                                        >
                                            <div className={classes.stepNumber}>
                                                {index + 1}
                                            </div>
                                            <div
                                                className={classes.stepContent}
                                            >
                                                <Typography
                                                    className={
                                                        classes.stepTitle
                                                    }
                                                >
                                                    {step.title}
                                                </Typography>
                                                <FormattedText
                                                    text={step.description}
                                                    details={step.details}
                                                    note={step.note}
                                                    link={step.link}
                                                />
                                                <div
                                                    className={
                                                        classes.screenshotContainer
                                                    }
                                                    style={{ marginTop: 16 }}
                                                >
                                                    <img
                                                        src={
                                                            step.image ||
                                                            `/placeholder.svg?height=240&width=400&text=Step ${index +
                                                                1 ||
                                                                "/placeholder.svg" ||
                                                                "/placeholder.svg"}: ${
                                                                step.title
                                                            }`
                                                        }
                                                        alt={`Screenshot for ${step.title}`}
                                                        className={
                                                            classes.screenshot
                                                        }
                                                        onError={e => {
                                                            // Fallback to placeholder if image fails to load
                                                            e.target.src = `/placeholder.svg?height=240&width=400&text=Step ${index +
                                                                1}: ${
                                                                step.title
                                                            }`;
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Paper>
                        )}

                        {/* FAQ Section */}
                        {activeSection === "faq" && (
                            <div>
                                <Box textAlign="center" mb={4}>
                                    <Typography
                                        variant="h3"
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: 16
                                        }}
                                    >
                                        Frequently Asked Questions
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        color="textSecondary"
                                    >
                                        Find quick answers to common questions
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    {faqs.map((faq, index) => (
                                        <Grid item xs={12} md={6} key={index}>
                                            <Paper className={classes.faqCard}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: 12
                                                    }}
                                                >
                                                    <HelpCircle
                                                        size={20}
                                                        color="#1DB584"
                                                        style={{
                                                            marginTop: 2,
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                    <div>
                                                        <Typography
                                                            variant="h6"
                                                            style={{
                                                                fontWeight: 600,
                                                                marginBottom: 8
                                                            }}
                                                        >
                                                            {faq.question}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            style={{
                                                                marginBottom: 8
                                                            }}
                                                        >
                                                            {renderFAQAnswer(
                                                                faq.answer
                                                            )}
                                                        </Typography>
                                                        <Chip
                                                            label={faq.category}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </div>
                                                </div>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>
                        )}

                        {/* Contact Support Section */}
                        {activeSection === "contact" && (
                            <div>
                                <Box textAlign="center" mb={4}>
                                    <Typography
                                        variant="h3"
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: 16
                                        }}
                                    >
                                        Contact Support
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        color="textSecondary"
                                    >
                                        Get help from our expert support team
                                    </Typography>
                                </Box>

                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={6}>
                                        <Paper className={classes.contactCard}>
                                            <div
                                                className={`${classes.contactIcon} ${classes.emailIcon}`}
                                            >
                                                <Mail size={32} />
                                            </div>
                                            <Typography
                                                variant="h5"
                                                style={{
                                                    fontWeight: 600,
                                                    marginBottom: 8
                                                }}
                                            >
                                                Email Support
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="textSecondary"
                                                style={{ marginBottom: 16 }}
                                            >
                                                Get detailed help via email
                                                within 24 hours
                                            </Typography>
                                            <Button
                                                className={`${classes.contactButton} ${classes.emailButton}`}
                                                endIcon={
                                                    <ExternalLink size={16} />
                                                }
                                            >
                                                Send Email
                                            </Button>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Paper className={classes.contactCard}>
                                            <div
                                                className={`${classes.contactIcon} ${classes.phoneIcon}`}
                                            >
                                                <Phone size={32} />
                                            </div>
                                            <Typography
                                                variant="h5"
                                                style={{
                                                    fontWeight: 600,
                                                    marginBottom: 8
                                                }}
                                            >
                                                Phone Support
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                color="textSecondary"
                                                style={{ marginBottom: 16 }}
                                            >
                                                Speak directly with our team
                                                during business hours
                                            </Typography>
                                            <Button
                                                className={`${classes.contactButton} ${classes.phoneButton}`}
                                                endIcon={
                                                    <ExternalLink size={16} />
                                                }
                                            >
                                                Call Now
                                            </Button>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Paper className={classes.emergencyCard}>
                                    <Typography
                                        variant="h6"
                                        style={{
                                            fontWeight: 600,
                                            marginBottom: 8
                                        }}
                                    >
                                        Need Immediate Help?
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        style={{ marginBottom: 16 }}
                                    >
                                        For urgent issues, please call our
                                        emergency hotline or check our status
                                        page for system updates.
                                    </Typography>
                                    <div className={classes.emergencyActions}>
                                        <Button
                                            variant="outlined"
                                            style={{
                                                borderColor: "#1DB584",
                                                color: "#1DB584"
                                            }}
                                        >
                                            Emergency Hotline
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            style={{
                                                borderColor: "#4A90E2",
                                                color: "#4A90E2"
                                            }}
                                        >
                                            System Status
                                        </Button>
                                    </div>
                                </Paper>
                            </div>
                        )}

                        {/* Category Overview */}
                        {(activeSection === "basic-operations" ||
                            activeSection === "work-order" ||
                            activeSection === "asset-management" ||
                            activeSection === "service-request" ||
                            activeSection === "sla-management" ||
                            activeSection === "administration" ||
                            activeSection === "pricebook-warehouse") && (
                            <div>
                                <Box textAlign="center" mb={4}>
                                    <Typography
                                        variant="h3"
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: 16
                                        }}
                                    >
                                        {activeSection === "basic-operations"
                                            ? "Basic Operations"
                                            : activeSection === "work-order"
                                            ? "Work Order"
                                            : activeSection ===
                                              "asset-management"
                                            ? "Asset Management"
                                            : activeSection ===
                                              "service-request"
                                            ? "Service Request"
                                            : activeSection === "sla-management"
                                            ? "SLA Management"
                                            : activeSection === "administration"
                                            ? "Administration"
                                            : "Pricebook & Warehouse"}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        color="textSecondary"
                                    >
                                        {activeSection === "basic-operations"
                                            ? "Learn the fundamental operations of ANIKE platform"
                                            : activeSection === "work-order"
                                            ? "Master work order and task management features"
                                            : activeSection ===
                                              "asset-management"
                                            ? "Comprehensive asset and cluster management guides"
                                            : activeSection ===
                                              "service-request"
                                            ? "Complete service request lifecycle from creation to completion"
                                            : activeSection === "sla-management"
                                            ? "Create and manage Service Level Agreements for service providers"
                                            : activeSection === "administration"
                                            ? "Manage users, roles, and organizational settings"
                                            : "Manage pricing catalogs and warehouse inventory for cost estimation and stock tracking"}
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    {guides
                                        .filter(
                                            guide =>
                                                guide.category ===
                                                (activeSection ===
                                                "basic-operations"
                                                    ? "Basic Operations"
                                                    : activeSection ===
                                                      "work-order"
                                                    ? "Work Order"
                                                    : activeSection ===
                                                      "asset-management"
                                                    ? "Asset Management"
                                                    : activeSection ===
                                                      "service-request"
                                                    ? "Service Request"
                                                    : activeSection ===
                                                      "sla-management"
                                                    ? "SLA Management"
                                                    : activeSection ===
                                                      "administration"
                                                    ? "Administration"
                                                    : "Pricebook & Warehouse")
                                        )
                                        .map(guide => (
                                            <Grid
                                                item
                                                xs={12}
                                                md={6}
                                                key={guide.id}
                                            >
                                                <Paper
                                                    className={
                                                        classes.categoryCard
                                                    }
                                                    onClick={() =>
                                                        setActiveSection(
                                                            guide.id
                                                        )
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            classes.categoryHeader
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                classes.categoryIcon
                                                            }
                                                        >
                                                            {guide.icon}
                                                        </div>
                                                        <div
                                                            style={{ flex: 1 }}
                                                        >
                                                            <Typography
                                                                variant="h6"
                                                                style={{
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                {guide.title}
                                                            </Typography>
                                                            <Chip
                                                                label={
                                                                    guide.difficulty
                                                                }
                                                                size="small"
                                                                className={`${
                                                                    classes.difficultyChip
                                                                } ${getDifficultyChipClass(
                                                                    guide.difficulty
                                                                )}`}
                                                            />
                                                        </div>
                                                    </div>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        style={{
                                                            marginBottom: 16
                                                        }}
                                                    >
                                                        {guide.steps.length}{" "}
                                                        steps •{" "}
                                                        {guide.difficulty}{" "}
                                                        difficulty
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        style={{
                                                            borderColor:
                                                                "#1DB584",
                                                            color: "#1DB584"
                                                        }}
                                                        endIcon={
                                                            <ChevronDown
                                                                size={16}
                                                                style={{
                                                                    transform:
                                                                        "rotate(-90deg)"
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        View Guide
                                                    </Button>
                                                </Paper>
                                            </Grid>
                                        ))}
                                </Grid>
                            </div>
                        )}

                        {/* Fault Reporting Category */}
                        {activeSection === "fault-reporting" && (
                            <div>
                                <Box textAlign="center" mb={4}>
                                    <Typography
                                        variant="h3"
                                        style={{
                                            fontWeight: 700,
                                            marginBottom: 16
                                        }}
                                    >
                                        Fault Reporting
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        color="textSecondary"
                                    >
                                        Learn how to report, manage, and track
                                        asset faults through the complete
                                        workflow
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    {guides
                                        .filter(
                                            guide =>
                                                guide.category ===
                                                "Fault Reporting"
                                        )
                                        .map(guide => (
                                            <Grid
                                                item
                                                xs={12}
                                                md={6}
                                                key={guide.id}
                                            >
                                                <Paper
                                                    className={
                                                        classes.categoryCard
                                                    }
                                                    onClick={() =>
                                                        setActiveSection(
                                                            guide.id
                                                        )
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            classes.categoryHeader
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                classes.categoryIcon
                                                            }
                                                        >
                                                            {guide.icon}
                                                        </div>
                                                        <div
                                                            style={{ flex: 1 }}
                                                        >
                                                            <Typography
                                                                variant="h6"
                                                                style={{
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                {guide.title}
                                                            </Typography>
                                                            <Chip
                                                                label={
                                                                    guide.difficulty
                                                                }
                                                                size="small"
                                                                className={`${
                                                                    classes.difficultyChip
                                                                } ${getDifficultyChipClass(
                                                                    guide.difficulty
                                                                )}`}
                                                            />
                                                        </div>
                                                    </div>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        style={{
                                                            marginBottom: 16
                                                        }}
                                                    >
                                                        {guide.steps.length}{" "}
                                                        steps •{" "}
                                                        {guide.difficulty}{" "}
                                                        difficulty
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        style={{
                                                            borderColor:
                                                                "#1DB584",
                                                            color: "#1DB584"
                                                        }}
                                                        endIcon={
                                                            <ChevronDown
                                                                size={16}
                                                                style={{
                                                                    transform:
                                                                        "rotate(-90deg)"
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        View Guide
                                                    </Button>
                                                </Paper>
                                            </Grid>
                                        ))}
                                </Grid>
                            </div>
                        )}

                        {/* Default Overview - shown when no specific section is selected */}
                        {!activeGuide &&
                            activeSection !== "faq" &&
                            activeSection !== "contact" &&
                            activeSection !== "basic-operations" &&
                            activeSection !== "fault-reporting" &&
                            activeSection !== "service-request" &&
                            activeSection !== "work-order" &&
                            activeSection !== "asset-management" &&
                            activeSection !== "sla-management" &&
                            activeSection !== "administration" &&
                            activeSection !== "pricebook-warehouse" && (
                                <div>
                                    <Box textAlign="center" mb={6}>
                                        <Typography
                                            variant="h3"
                                            style={{
                                                fontWeight: 700,
                                                marginBottom: 16
                                            }}
                                        >
                                            Welcome to ANIKE Help Center
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            color="textSecondary"
                                            style={{ marginBottom: 32 }}
                                        >
                                            Choose a category from the sidebar
                                            to get started, or browse our most
                                            popular guides below
                                        </Typography>
                                    </Box>

                                    {/* Navigation Categories Overview */}
                                    <Typography
                                        variant="h4"
                                        style={{
                                            fontWeight: 600,
                                            marginBottom: 24
                                        }}
                                    >
                                        Help Categories
                                    </Typography>

                                    <Grid
                                        container
                                        spacing={3}
                                        style={{ marginBottom: 48 }}
                                    >
                                        {categories
                                            .filter(
                                                cat =>
                                                    cat.id !== "faq" &&
                                                    cat.id !== "contact"
                                            )
                                            .map(category => (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    md={6}
                                                    lg={4}
                                                    key={category.id}
                                                >
                                                    <Paper
                                                        className={
                                                            classes.categoryCard
                                                        }
                                                        onClick={() =>
                                                            setActiveSection(
                                                                category.id
                                                            )
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                classes.categoryHeader
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    classes.categoryIcon
                                                                }
                                                            >
                                                                {category.icon}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    flex: 1
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        marginBottom: 8
                                                                    }}
                                                                >
                                                                    {
                                                                        category.name
                                                                    }
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="textSecondary"
                                                                >
                                                                    {category.id ===
                                                                        "basic-operations" &&
                                                                        "Learn the fundamental operations of ANIKE platform"}
                                                                    {category.id ===
                                                                        "fault-reporting" &&
                                                                        "Report, manage, and track asset faults"}
                                                                    {category.id ===
                                                                        "service-request" &&
                                                                        "Complete service request lifecycle management"}
                                                                    {category.id ===
                                                                        "work-order" &&
                                                                        "Master work order and task management"}
                                                                    {category.id ===
                                                                        "asset-management" &&
                                                                        "Comprehensive asset and cluster management"}
                                                                    {category.id ===
                                                                        "sla-management" &&
                                                                        "Create and manage Service Level Agreements"}
                                                                    {category.id ===
                                                                        "administration" &&
                                                                        "Manage users, roles, and settings"}
                                                                    {category.id ===
                                                                        "pricebook-warehouse" &&
                                                                        "Manage pricing catalogs and warehouse inventory"}
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                            style={{
                                                                marginTop: 12
                                                            }}
                                                        >
                                                            {
                                                                guides.filter(
                                                                    g =>
                                                                        g.category ===
                                                                        (category.id ===
                                                                        "basic-operations"
                                                                            ? "Basic Operations"
                                                                            : category.id ===
                                                                              "fault-reporting"
                                                                            ? "Fault Reporting"
                                                                            : category.id ===
                                                                              "service-request"
                                                                            ? "Service Request"
                                                                            : category.id ===
                                                                              "work-order"
                                                                            ? "Work Order"
                                                                            : category.id ===
                                                                              "asset-management"
                                                                            ? "Asset Management"
                                                                            : category.id ===
                                                                              "sla-management"
                                                                            ? "SLA Management"
                                                                            : category.id ===
                                                                              "administration"
                                                                            ? "Administration"
                                                                            : "Pricebook & Warehouse")
                                                                ).length
                                                            }{" "}
                                                            guides available
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                    </Grid>

                                    {/* Quick Access to Popular Guides */}
                                    <Typography
                                        variant="h4"
                                        style={{
                                            fontWeight: 600,
                                            marginBottom: 24
                                        }}
                                    >
                                        Popular Guides
                                    </Typography>

                                    <Grid container spacing={3}>
                                        {guides.slice(0, 6).map(guide => (
                                            <Grid
                                                item
                                                xs={12}
                                                md={6}
                                                key={guide.id}
                                            >
                                                <Paper
                                                    className={
                                                        classes.categoryCard
                                                    }
                                                    onClick={() =>
                                                        setActiveSection(
                                                            guide.id
                                                        )
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            classes.categoryHeader
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                classes.categoryIcon
                                                            }
                                                        >
                                                            {guide.icon}
                                                        </div>
                                                        <div
                                                            style={{ flex: 1 }}
                                                        >
                                                            <Typography
                                                                variant="h6"
                                                                style={{
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                {guide.title}
                                                            </Typography>
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "flex",
                                                                    gap: 8,
                                                                    alignItems:
                                                                        "center",
                                                                    marginTop: 4
                                                                }}
                                                            >
                                                                <Chip
                                                                    label={
                                                                        guide.difficulty
                                                                    }
                                                                    size="small"
                                                                    className={`${
                                                                        classes.difficultyChip
                                                                    } ${getDifficultyChipClass(
                                                                        guide.difficulty
                                                                    )}`}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                >
                                                                    {
                                                                        guide.category
                                                                    }
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        style={{
                                                            marginBottom: 16
                                                        }}
                                                    >
                                                        {guide.steps.length}{" "}
                                                        steps •{" "}
                                                        {guide.difficulty}{" "}
                                                        difficulty
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        fullWidth
                                                        style={{
                                                            borderColor:
                                                                "#1DB584",
                                                            color: "#1DB584"
                                                        }}
                                                        endIcon={
                                                            <ChevronDown
                                                                size={16}
                                                                style={{
                                                                    transform:
                                                                        "rotate(-90deg)"
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        View Guide
                                                    </Button>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </div>
                            )}
                    </Grid>
                </Grid>
            </Container>

            <Drawer
                anchor="left"
                open={mobileNavOpen}
                onClose={() => setMobileNavOpen(false)}
                classes={{ paper: classes.drawerPaper }}
            >
                <div className={classes.drawerHeader}>
                    <Typography className={classes.title}>
                        Help Center
                    </Typography>
                    <IconButton
                        aria-label="Close help menu"
                        onClick={() => setMobileNavOpen(false)}
                    >
                        <CloseIcon size={20} />
                    </IconButton>
                </div>
                <Input
                    className={classes.drawerSearch}
                    placeholder="Search guides..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    startAdornment={
                        <InputAdornment position="start">
                            <Search size={16} color="#999" />
                        </InputAdornment>
                    }
                />
                <div className={classes.drawerBody}>{sidebarNav}</div>
            </Drawer>
        </div>
    );
}
